import { useLayoutEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { CircleHelp, Clapperboard, Code2, LayoutGrid, Menu, Shield, Users } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { useDevAuth } from "@/context/DevAuthContext";
import { Wordmark } from "@/components/brand/Wordmark";
import { LanguageMenu } from "@/components/layout/LanguageMenu";
import { InstallAppButton } from "@/components/pwa/InstallApp";
import { NewsPushButton } from "@/components/pwa/NewsPush";
import { useI18n } from "@/i18n/I18nContext";

const navCls = ({ isActive }) =>
  `inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors duration-200 ${
    isActive ? "bg-[rgba(245,197,66,0.15)] text-[var(--gold-bright)]" : "text-neutral-300 hover:text-[var(--gold-bright)]"
  }`;

const devCls =
  "inline-flex items-center gap-2 rounded-full border-2 border-[var(--gold)] bg-[rgba(245,197,66,0.08)] px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[var(--gold-bright)] shadow-[0_0_14px_rgba(245,197,66,0.25)] transition-colors duration-200 hover:bg-[rgba(245,197,66,0.2)]";

function useLinks() {
  const { user } = useAuth();
  const { dev } = useDevAuth();
  const { t } = useI18n();
  const { marketplace } = useSite();
  return {
    marketplace,
    devTo: dev ? "/espace-developpeur" : "/espace-developpeur/connexion",
    devLabel: t(dev ? "nav.mySpace" : "nav.devSpace"),
    links: [
      { to: "/apps", icon: LayoutGrid, label: t("nav.apps"), id: "nav-apps-link" },
      { to: "/videos", icon: Clapperboard, label: t("nav.videos"), id: "nav-videos-link" },
      marketplace && { to: "/developpeurs", icon: Users, label: t("nav.others"), id: "nav-others-link" },
      { to: "/installer-un-apk", icon: CircleHelp, label: t("nav.install"), id: "nav-install-link" },
      { to: user ? "/admin" : "/admin/login", icon: Shield, label: t("nav.admin"), id: "nav-admin-link" },
    ].filter(Boolean),
  };
}

const MobileMenu = () => {
  const { t } = useI18n();
  const { links, devTo, devLabel, marketplace } = useLinks();
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger className="rounded-full p-2 text-neutral-200 hover:text-[var(--gold-bright)] md:hidden" aria-label={t("nav.menu")} data-testid="mobile-menu-btn">
        <Menu className="h-6 w-6" />
      </SheetTrigger>
      <SheetContent side="right" className="w-72 max-w-[calc(100vw-1.5rem)] overflow-y-auto border-[rgba(245,197,66,0.25)] bg-[var(--carbon)] text-white" data-testid="mobile-menu">
        <SheetTitle className="font-display text-sm uppercase tracking-widest text-[var(--gold)]">{t("nav.menu")}</SheetTitle>
        <nav className="mt-6 flex flex-col gap-3" onClick={() => setOpen(false)}>
          {marketplace && <Link to={devTo} className={`${devCls} justify-center py-3`} data-testid="mobile-dev-space-link"><Code2 className="h-4 w-4" /> {devLabel}</Link>}
          {links.map(({ to, icon: Icon, label, id }) => (
            <NavLink key={id} to={to} className={(s) => `${navCls(s)}${id === "nav-videos-link" ? " videos-glow" : ""}`} data-testid={`mobile-${id}`}><Icon className="h-4 w-4" /> {id === "nav-admin-link" ? t("nav.administration") : label}</NavLink>
          ))}
        </nav>
        <div onClickCapture={() => setOpen(false)}><InstallAppButton variant="menu" className="mt-6 w-full" /></div>
        <NewsPushButton className="mt-4" testId="menu-news-push-btn" />
      </SheetContent>
    </Sheet>
  );
};

// Desktop nav labels are shown only when brand + full nav fit side by side (any language); otherwise icons only.
function useCompactNav(locale) {
  const row = useRef(null);
  const brand = useRef(null);
  const nav = useRef(null);
  const full = useRef(0);
  const [compact, setCompact] = useState(false);
  useLayoutEffect(() => {
    full.current = 0;
    setCompact(false);
  }, [locale]);
  useLayoutEffect(() => {
    const check = () => {
      if (!row.current || !brand.current || !nav.current) return;
      if (!compact) full.current = nav.current.scrollWidth;
      const cs = getComputedStyle(row.current);
      const room = row.current.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - parseFloat(cs.columnGap || 0);
      setCompact(brand.current.scrollWidth + full.current > room);
    };
    check();
    const ro = new ResizeObserver(check);
    [row, brand, nav].forEach((r) => r.current && ro.observe(r.current));
    document.fonts?.ready.then(check);
    return () => ro.disconnect();
  }, [compact, locale]);
  return { row, brand, nav, compact };
}

export const SiteHeader = () => {
  const { logoUrl } = useSite();
  const { locale } = useI18n();
  const { links, devTo, devLabel, marketplace } = useLinks();
  const { row, brand, nav, compact } = useCompactNav(locale);
  return (
    <header className="sticky top-0 z-40 border-b border-[rgba(245,197,66,0.25)] bg-[rgba(10,8,9,0.92)] backdrop-blur-lg">
      <div ref={row} className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-8 lg:gap-8 2xl:max-w-[1536px]">
        <Link ref={brand} to="/" className="flex min-w-0 flex-1 items-center gap-[8px] sm:gap-3 max-lg:[container-type:inline-size] lg:flex-none lg:shrink-0" data-testid="header-home-link">
          <img
            src={logoUrl}
            alt="Logo FAB-MIND-OS"
            className="h-[36px] w-[36px] shrink-0 rounded-xl object-cover min-[360px]:h-[40px] min-[360px]:w-[40px] ring-1 ring-[rgba(245,197,66,0.5)] shadow-[0_0_16px_rgba(208,20,31,0.45)] sm:h-11 sm:w-11"
            data-testid="header-logo"
          />
          <Wordmark size="header" testId="header-wordmark" />
        </Link>
        <nav ref={nav} className="flex shrink-0 items-center gap-0.5 sm:gap-1" data-testid="header-nav" data-compact={compact ? "true" : "false"}>
          {links.map(({ to, icon: Icon, label, id }) => (
            <NavLink key={id} to={to} className={(s) => `${navCls(s)} hidden md:inline-flex${id === "nav-videos-link" ? " videos-glow" : ""}`} aria-label={label} title={label} data-testid={id}>
              <Icon className="h-4 w-4" />
              <span className={compact ? "hidden" : "hidden lg:inline"}>{label}</span>
            </NavLink>
          ))}
          {marketplace && (
            <Link to={devTo} className={`${devCls} ms-1 hidden sm:inline-flex`} data-testid="nav-dev-space-btn">
              <Code2 className="h-4 w-4 shrink-0" /> {devLabel}
            </Link>
          )}
          <InstallAppButton variant="header" className="ms-1" />
          <LanguageMenu />
          <MobileMenu />
        </nav>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-[rgba(245,197,66,0.15)] px-4 py-2 empty:hidden sm:hidden" data-testid="header-mobile-row">
        {marketplace && (
          <Link to={devTo} className={`${devCls} flex-auto justify-center`} data-testid="nav-dev-space-btn-mobile">
            <Code2 className="h-4 w-4 shrink-0" /> {devLabel}
          </Link>
        )}
        <InstallAppButton variant="mobile" className={marketplace ? "flex-auto" : "flex-1"} />
      </div>
    </header>
  );
};
